import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminSidebar, StatCard, SkeletonCard, SkeletonTable, PageHeader } from "@/components/AdminLayout";
import api from "@/utils/api";

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [subscriptionStats, setSubscriptionStats] = useState({
    mrr: 0,
    activeSubscriptions: 0,
    trialClients: 0,
    renewalsDue: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, clientsRes, usersRes, coursesRes, progressRes] = await Promise.all([
        api.get("/analytics/dashboard"),
        api.get("/clients"),
        api.get("/users"),
        api.get("/courses"),
        api.get("/progress/all").catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      
      const users = usersRes.data || [];
      const courses = coursesRes.data || [];
      const progress = progressRes.data || [];
      
      // Enhance clients with subscription and stats data
      const enhancedClients = clientsRes.data.map(client => {
        const clientWorkers = users.filter(u => u.client_id === client.id && u.role === 'worker');
        const clientAdmins = users.filter(u => u.client_id === client.id && u.role === 'client_admin');
        const clientCourses = courses.filter(c => c.client_id === client.id);
        const clientProgress = progress.filter(p => clientWorkers.find(w => w.id === p.worker_id));
        const completedCount = clientProgress.filter(p => p.completed).length;
        const totalAssignments = clientProgress.length;
        const completionRate = totalAssignments > 0 ? Math.round((completedCount / totalAssignments) * 100) : 0;
        
        // Mock subscription data (would come from backend in production)
        const subscriptionPlans = ['Basic', 'Standard', 'Enterprise'];
        const planIndex = Math.floor(Math.random() * 3);
        const planPrices = { Basic: 299, Standard: 599, Enterprise: 999 };
        const plan = subscriptionPlans[planIndex];
        const statuses = ['Active', 'Active', 'Active', 'Trial', 'Overdue'];
        const status = statuses[Math.floor(Math.random() * 5)];
        
        return {
          ...client,
          workers: clientWorkers.length,
          admins: clientAdmins.length,
          courses: clientCourses.length,
          completionRate,
          subscription: {
            plan,
            amount: planPrices[plan],
            status,
            startDate: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: new Date(Date.now() + Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString()
          }
        };
      });
      
      setClients(enhancedClients);
      
      // Calculate subscription stats
      const activeClients = enhancedClients.filter(c => c.subscription.status === 'Active');
      const trialClients = enhancedClients.filter(c => c.subscription.status === 'Trial');
      const mrr = activeClients.reduce((sum, c) => sum + c.subscription.amount, 0);
      const renewalsDue = enhancedClients.filter(c => {
        const endDate = new Date(c.subscription.endDate);
        const now = new Date();
        const daysUntilRenewal = (endDate - now) / (1000 * 60 * 60 * 24);
        return daysUntilRenewal <= 30 && daysUntilRenewal > 0;
      }).length;
      
      setSubscriptionStats({
        mrr,
        activeSubscriptions: activeClients.length,
        trialClients: trialClients.length,
        renewalsDue
      });
      
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      Active: 'badge-success',
      Trial: 'badge-info',
      Overdue: 'badge-danger'
    };
    return <span className={`badge ${styles[status] || 'badge-neutral'}`}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="flex">
        <AdminSidebar active="dashboard" role="super_admin" />
        <main className="admin-content">
          <div className="mb-6">
            <div className="h-8 bg-slate-200 rounded w-64 mb-2 animate-pulse"></div>
            <div className="h-4 bg-slate-200 rounded w-48 animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
          <SkeletonTable rows={5} />
        </main>
      </div>
    );
  }

  return (
    <div className="flex">
      <AdminSidebar active="dashboard" role="super_admin" />
      <main className="admin-content">
        <PageHeader 
          title="Platform Dashboard" 
          subtitle="VisualOps Training Portal • System-wide overview"
        />

        {/* Platform Stats - 4 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
            iconBg="bg-blue-100"
            value={clients.length}
            label="Total Clients"
          />
          <StatCard
            icon={<svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
            iconBg="bg-green-100"
            value={stats?.total_workers || 0}
            label="Total Workers"
          />
          <StatCard
            icon={<svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>}
            iconBg="bg-purple-100"
            value={stats?.total_courses || 0}
            label="Total Courses"
          />
          <StatCard
            icon={<svg className="w-6 h-6 text-teal-600" fill="currentColor" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            iconBg="bg-teal-100"
            value={stats?.completed_trainings || 0}
            label="Completions"
          />
        </div>

        {/* Subscription Overview - 4 columns */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Subscription Overview</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              iconBg="bg-emerald-100"
              value={`$${subscriptionStats.mrr.toLocaleString()}`}
              label="Monthly MRR"
            />
            <StatCard
              icon={<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              iconBg="bg-blue-100"
              value={subscriptionStats.activeSubscriptions}
              label="Active Subscriptions"
            />
            <StatCard
              icon={<svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              iconBg="bg-amber-100"
              value={subscriptionStats.trialClients}
              label="Trial Clients"
            />
            <StatCard
              icon={<svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
              iconBg="bg-red-100"
              value={subscriptionStats.renewalsDue}
              label="Renewals Due (30d)"
            />
          </div>
        </div>

        {/* Clients Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">All Clients</h2>
            <button 
              onClick={() => navigate('/super-admin/clients')}
              className="text-sm text-[#2e7fd4] hover:text-[#1c64b0] font-medium"
            >
              View All →
            </button>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Plan</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Workers</th>
                  <th>Courses</th>
                  <th>Completion</th>
                  <th>Renewal Date</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr 
                    key={client.id} 
                    className="cursor-pointer"
                    onClick={() => navigate(`/super-admin/clients/${client.id}`)}
                  >
                    <td>
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                          style={{ backgroundColor: client.brand_color || '#0a2240' }}
                        >
                          {client.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{client.name}</p>
                          <p className="text-xs text-slate-500">{client.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${
                        client.subscription.plan === 'Enterprise' ? 'badge-info' :
                        client.subscription.plan === 'Standard' ? 'badge-neutral' : 'badge-neutral'
                      }`}>
                        {client.subscription.plan}
                      </span>
                    </td>
                    <td className="font-semibold">${client.subscription.amount}/mo</td>
                    <td>{getStatusBadge(client.subscription.status)}</td>
                    <td>{client.workers}</td>
                    <td>{client.courses}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#0e9f8e] rounded-full" 
                            style={{width: `${client.completionRate}%`}}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">{client.completionRate}%</span>
                      </div>
                    </td>
                    <td className="text-sm">
                      {new Date(client.subscription.endDate).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
